/**
 * Who is typing.
 *
 * The server has relayed a `typing` event for direct messages and party
 * channels since the beginning, and the composers have always sent it — and
 * nothing in the client has ever listened. So the feature existed end to end
 * except for the part that draws it, in every conversation, the whole time.
 *
 * One component for all three kinds. What differs between them is only which
 * events belong to the conversation on screen, so that is the one thing the
 * caller passes in.
 */

import { useSession } from '@/stores/session';
import { useEffect, useState } from 'react';
import { socket } from '@/lib/api';
import type { PublicUser } from '@/lib/types';

/** How long a name stays up after the last keystroke that mentioned it. */
const HOLD_MS = 4500;

type Ping = { scope: string; channelId?: string; user: PublicUser };

export function TypingLine({ match }: {
  /** True when an event belongs to the conversation being drawn. */
  match: (ping: Ping) => boolean;
}) {
  const [who, setWho] = useState<{ user: PublicUser; at: number }[]>([]);
  /* Typing indicators can be turned off in Chat settings. */
  const hidden = useSession((s) => s.settings?.typingIndicators === false);

  useEffect(() => {
    const off = socket.on('typing', (raw: unknown) => {
      const ping = raw as Ping;
      if (!ping?.user || !match(ping)) return;
      setWho((list) => [
        ...list.filter((w) => w.user.id !== ping.user.id),
        { user: ping.user, at: Date.now() }
      ]);
    });
    return off;
  }, [match]);

  /*
   * Names age out rather than being cleared by a stop event.
   *
   * There is no "stopped typing" message and there should not be - it doubles
   * the traffic to say nothing. A keystroke refreshes the timestamp, so
   * somebody who is still typing stays up and somebody who wandered off drops
   * away on their own.
   */
  useEffect(() => {
    if (!who.length) return;
    const t = setInterval(() => {
      setWho((list) => {
        const live = list.filter((w) => Date.now() - w.at < HOLD_MS);
        return live.length === list.length ? list : live;
      });
    }, 700);
    return () => clearInterval(t);
  }, [who.length]);

  if (!who.length || hidden) return null;

  const names = who.map((w) => w.user.displayName);
  const said =
    names.length === 1 ? names[0] + ' is typing'
      : names.length === 2 ? names[0] + ' and ' + names[1] + ' are typing'
        : names.length + ' people are typing';

  return (
    <div
      /* Polite, so a screen reader is told without being interrupted. */
      aria-live="polite"
      className="flex h-[18px] flex-none items-center gap-1.5 px-[var(--pad)] pb-1 text-[11.5px] text-mute"
    >
      <span className="flex items-end gap-[2px] pb-[2px]">
        <Dot delay={0} />
        <Dot delay={140} />
        <Dot delay={280} />
      </span>
      <span className="truncate">{said}</span>
    </div>
  );
}

/** One bouncing dot. Three of them, offset, read as a rhythm. */
function Dot({ delay }: { delay: number }) {
  return (
    <span
      className="block h-[3px] w-[3px] rounded-full bg-mute"
      style={{
        animation: 'typing-bob 1s var(--ease-out-soft, ease) infinite',
        animationDelay: delay + 'ms'
      }}
    />
  );
}
